import { FormEvent, useCallback, useMemo, useState } from 'react'
import { useNotify, useTranslate } from 'react-admin'
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CloseIcon from '@mui/icons-material/Close'
import { DashboardContent } from 'src/layouts/dashboard'
import { PermissionGuard } from 'src/routes/components/permission-guard'
import ErrorPage403 from 'src/pages/error/403'
import { useTurnkeyAddressSearch } from './hooks/useTurnkeyAddressSearch'
import CopyCell from '../dashboard-v3/components/CopyCell'

type CopyValueProps = {
    value?: string
}

function CopyValue({ value }: CopyValueProps) {
    const notify = useNotify()

    const handleCopy = useCallback(
        async (event: React.MouseEvent) => {
            event.stopPropagation()

            if (!value) {
                return
            }

            await navigator.clipboard.writeText(value)
            notify('Copied', { type: 'success' })
        },
        [notify, value],
    )

    if (!value) {
        return <Typography>-</Typography>
    }

    return (
        <Stack direction="row" alignItems="center" spacing={0.75}>
            <Typography
                component="span"
                sx={{
                    fontSize: 13,
                    fontFamily: 'monospace',
                    wordBreak: 'break-all',
                }}
            >
                {value}
            </Typography>

            <Tooltip title="Copy">
                <IconButton
                    size="small"
                    onClick={handleCopy}
                    sx={{
                        color: 'rgba(255,255,255,0.56)',
                        '&:hover': {
                            color: '#00A76F',
                            bgcolor: 'rgba(187,134,252,0.08)',
                        },
                    }}
                >
                    <ContentCopyIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
        </Stack>
    )
}

export const TurnkeyAddressSearchPage = () => {
    const t = useTranslate()

    const [keyword, setKeyword] = useState('')
    const { loading, searched, items, searchedTotal, matchedTotal, search, reset } = useTurnkeyAddressSearch()

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault()
            await search(keyword)
        },
        [keyword, search],
    )

    const handleClear = useCallback(() => {
        setKeyword('')
        reset()
    }, [reset])

    const hasKeyword = Boolean(keyword.trim())
    const lineNumbers = useMemo(() => {
        const lines = keyword ? keyword.split(/\r\n|\r|\n/) : ['']
        return lines.map((_, index) => index + 1)
    }, [keyword])

    return (
        <PermissionGuard
            resource="turnkey-address-search"
            action="read"
            fallback={<ErrorPage403 />}
        >
            <DashboardContent sx={{ pb: 50, bgcolor: 'transparent' }}>
                <Box sx={{ mt: 3 }}>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                        spacing={2}
                        sx={{ mb: 3 }}
                    >
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {t('resource.turnkeyAddressSearch', {
                                    _: 'Turnkey Address Search',
                                })}
                            </Typography>

                            <Typography>
                                {t('module.turnkeyAddressSearch.subtitle', {
                                    _: 'Search by deposit address, prediction address, leverage address, login wallet or email, then return the Turnkey EVM address.',
                                })}
                            </Typography>
                        </Box>
                    </Stack>

                    <Paper
                        sx={{
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: 2,
                            p: 2.5,
                            mb: 3,
                        }}
                    >
                        <Box component="form" onSubmit={handleSubmit}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={2}
                                alignItems="stretch"
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flex: 1,
                                        minHeight: 280,
                                        border: '1px solid rgba(255,255,255,0.16)',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        bgcolor: 'rgba(0,0,0,0.08)',
                                        '&:focus-within': {
                                            borderColor: '#00A76F',
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 52,
                                            py: 1.5,
                                            color: 'rgba(255,255,255,0.36)',
                                            bgcolor: 'rgba(255,255,255,0.03)',
                                            borderRight: '1px solid rgba(255,255,255,0.08)',
                                            textAlign: 'right',
                                            userSelect: 'none',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {lineNumbers.map((lineNumber) => (
                                            <Typography
                                                key={lineNumber}
                                                sx={{
                                                    pr: 1.5,
                                                    fontSize: 13,
                                                    lineHeight: '22px',
                                                    fontFamily: 'monospace',
                                                }}
                                            >
                                                {lineNumber}
                                            </Typography>
                                        ))}
                                    </Box>

                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={12}
                                        maxRows={18}
                                        value={keyword}
                                        onChange={(event) => setKeyword(event.target.value)}
                                        placeholder={t('module.turnkeyAddressSearch.placeholder', {
                                            _: 'One address, wallet or email per line',
                                        })}
                                        variant="standard"
                                        InputProps={{
                                            disableUnderline: true,
                                            sx: {
                                                alignItems: 'flex-start',
                                                fontFamily: 'monospace',
                                                fontSize: 13,
                                                lineHeight: '22px',
                                                p: 1.5,
                                            },
                                        }}
                                        sx={{
                                            '& textarea': {
                                                lineHeight: '22px',
                                            },
                                        }}
                                    />
                                </Box>

                                <Stack spacing={1.25} sx={{ width: { xs: '100%', md: 132 } }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        startIcon={!loading ? <SearchIcon sx={{ fontSize: 18 }} /> : undefined}
                                        disabled={loading}
                                        sx={{
                                            height: 44,
                                            textTransform: 'none',
                                            bgcolor: '#00A76F',
                                            fontWeight: 700,
                                            '&:hover': {
                                                bgcolor: '#03c987',
                                            },
                                        }}
                                    >
                                        {loading ? (
                                            <CircularProgress size={20} color="inherit" />
                                        ) : (
                                            t('module.turnkeyAddressSearch.search', { _: 'Search' })
                                        )}
                                    </Button>

                                    <Button
                                        type="button"
                                        variant="outlined"
                                        startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                                        disabled={!hasKeyword || loading}
                                        onClick={handleClear}
                                        sx={{
                                            height: 40,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {t('module.turnkeyAddressSearch.clear', { _: 'Clear' })}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Box>
                    </Paper>

                    <Paper
                        sx={{
                            border: '1px solid rgba(255,255,255,0.08)',
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            justifyContent="space-between"
                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                            spacing={1}
                            sx={{
                                px: 2,
                                py: 1.5,
                                borderBottom: '1px solid rgba(255,255,255,0.08)',
                            }}
                        >
                            <Typography sx={{ fontWeight: 700 }}>
                                {t('module.turnkeyAddressSearch.result', {
                                    _: 'Search Result',
                                })}
                            </Typography>

                            <Typography sx={{ color: 'rgba(255,255,255,0.56)', fontSize: 13 }}>
                                {t('module.turnkeyAddressSearch.total', {
                                    _: 'Searches: %{searchedTotal} · Matched: %{matchedTotal}',
                                    searchedTotal,
                                    matchedTotal,
                                })}
                            </Typography>
                        </Stack>

                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            '& th': {
                                                borderBottom: '1px solid rgba(255,255,255,0.08)',
                                                fontWeight: 700,
                                            },
                                        }}
                                    >
                                        <TableCell width="18%">
                                            {t('module.turnkeyAddressSearch.userId', {
                                                _: 'User ID',
                                            })}
                                        </TableCell>

                                        <TableCell width="24%">
                                            {t('module.turnkeyAddressSearch.keyword', {
                                                _: 'Query Item',
                                            })}
                                        </TableCell>

                                        <TableCell width="24%">
                                            {t('module.turnkeyAddressSearch.username', {
                                                _: 'Login Account / Wallet',
                                            })}
                                        </TableCell>

                                        <TableCell>
                                            {t('module.turnkeyAddressSearch.turnkeyAddress', {
                                                _: 'Turnkey EVM Address',
                                            })}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {items.map((row, index) => (
                                        <TableRow
                                            key={`${row.userId}-${row.keyword}-${row.turnkeyAddress}-${index}`}
                                            hover
                                            sx={{
                                                '& td': {
                                                    borderBottom:
                                                        '1px solid rgba(255,255,255,0.06)',
                                                },
                                                '&:hover': {
                                                    bgcolor: 'rgba(187,134,252,0.06)',
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                <CopyCell
                                                    text={row.userId}
                                                    copiedText={t('module.dashboardV3.common.copied', {
                                                        _: 'Copied',
                                                    })}
                                                    minWidth={112}
                                                />
                                            </TableCell>

                                            <TableCell>
                                                <CopyValue value={row.keyword} />
                                            </TableCell>

                                            <TableCell>
                                                <CopyValue value={row.username} />
                                            </TableCell>

                                            <TableCell>
                                                <CopyValue value={row.turnkeyAddress} />
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {!loading && searched && items.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Box sx={{ py: 5, textAlign: 'center' }}>
                                                    <Typography>
                                                        {t('module.turnkeyAddressSearch.empty', {
                                                            _: 'No matched user found',
                                                        })}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {!loading && !searched && (
                                        <TableRow>
                                            <TableCell colSpan={4}>
                                                <Box sx={{ py: 5, textAlign: 'center' }}>
                                                    <Typography>
                                                        {t('module.turnkeyAddressSearch.initialEmpty', {
                                                            _: 'Please enter address / email / wallet to search',
                                                        })}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Box>
            </DashboardContent>
        </PermissionGuard>
    )
}
